import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby-plugin-react-intl';
import styled from 'styled-components';
import get from 'lodash/get';

import colors from '@utils/colors';
import fonts from '@utils/fonts';
import NavSection from './NavSection';

const Wrapper = styled.div`
  font-family: ${props => fonts(props.theme.locale).SANS};
  font-weight: bold;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  padding-right: ${(props) => (props.primary ? '20px' : '24px')};
  padding-left: ${(props) => (props.primary ? '20px' : '24px')};

  & svg {
    vertical-align: middle;
  }

  & > a {
    font-size: 16px;
    padding: 8px 0;

    &.active {
      border-bottom: 2px solid ${(props) => getBorderColor(props.colorTheme)};
    }

    &.expanded {
      color: ${(props) => getTextColor(props.colorTheme)}
    }
  }
`;

const NavSectionWrapper = styled.div`
  background: ${colors.N0};
  position: absolute;
  padding: 17px 18px;
  top: -16px;
  left: 0;
  width: 195px;
  border: 2px solid ${(props) => getThemeStyles(props.colorTheme).background};
  z-index: 2;
`;

const NavItem = React.forwardRef((props, ref) => {
  const { children, onMouseEnter, primary, route, colorTheme, activeItem, idx, item } = props;

  return (
    <Wrapper
      onMouseEnter={onMouseEnter}
      primary={primary}
      ref={ref}
      colorTheme={colorTheme}
    >
      {children || (
        <>
          <Link
            to={get(route, 'slug.current')}
            activeClassName="active"
            className={activeItem === idx ? 'expanded' : ''}
            partiallyActive
          >
            {get(route, 'title')}
          </Link>
          {activeItem === idx && (
            <NavSectionWrapper
              key={activeItem}
              colorTheme={colorTheme}
              exit="exit"
              initial="enter"
            >
              <NavSection {...item} />
            </NavSectionWrapper>
          )}
        </>
      )}
    </Wrapper>
  );
});

NavItem.propTypes = {
  children: PropTypes.node,
  href: PropTypes.string,
  items: PropTypes.array,
  onMouseEnter: PropTypes.func,
  primary: PropTypes.bool,
  title: PropTypes.string,
};

export default NavItem;

function getTextColor(colorTheme) {
  switch (colorTheme) {
    case 'yellow':
      return colors.B500;
    case 'green':
      return colors.G500;
    case 'orange':
      return colors.O500;
    case 'purple':
      return colors.P500;
    default:
      return colors.B500;
  }
}

function getBorderColor(colorTheme) {
  switch (colorTheme) {
    case 'yellow':
      return colors.B200;
    case 'green':
      return colors.G200;
    case 'orange':
      return colors.O200;
    case 'purple':
      return colors.P200;
    default:
      return colors.B200;
  }
}

function getThemeStyles(colorTheme) {
  switch (colorTheme) {
    case 'blue':
      return {
        background: colors.B100,
        text: colors.B500,
      };
    case 'green':
      return {
        background: colors.G100,
        text: colors.G500,
      };
    case 'orange':
      return {
        background: colors.O100,
        text: colors.O500,
      };
    case 'purple':
      return {
        background: colors.P100,
        text: colors.P500,
      };
    default:
      return {
        background: colors.B100,
        text: colors.B500,
      };
  }
}
